/**
 * Офлайн-хранилище PMTiles-архивов.
 *
 * Зачем это вообще нужно: кадастровые инженеры и инспекторы градостроительного
 * контроля выезжают на участки, где сотовой сети либо нет, либо она не тянет.
 * Скачанный архив читается ровно тем же протоколом pmtiles — просто диапазоны
 * байт берутся из файла на устройстве, а не по HTTP.
 *
 * Ключевой приём: офлайн-источник регистрируется в протоколе под ТЕМ ЖЕ
 * ключом, что и сетевой URL. Благодаря этому стиль карты менять не нужно —
 * ссылка `pmtiles://https://.../basemap.pmtiles` сама начинает читать с диска.
 *
 * Хранилище: OPFS, а где его нет — IndexedDB. Оба отдают Blob, а срез Blob
 * ленив, поэтому чтение диапазона не поднимает в память весь архив.
 */

import { PMTiles, type Protocol, type RangeResponse, type Source } from "pmtiles";

const OPFS_DIR = "genplan-tiles";
const IDB_NAME = "genplan-offline";
const IDB_STORE = "archives";

export interface CachedArchive {
  url: string;
  sizeBytes: number;
}

export interface DownloadProgress {
  receivedBytes: number;
  totalBytes: number | null;
}

/** Источник PMTiles поверх локального Blob. */
class BlobSource implements Source {
  constructor(
    private readonly blob: Blob,
    private readonly key: string,
  ) {}

  getKey(): string {
    return this.key;
  }

  async getBytes(offset: number, length: number): Promise<RangeResponse> {
    const slice = this.blob.slice(offset, offset + length);
    return { data: await slice.arrayBuffer() };
  }
}

interface ArchiveStore {
  readonly kind: "opfs" | "indexeddb";
  save(name: string, body: ReadableStream<Uint8Array>, onChunk: (bytes: number) => void): Promise<number>;
  read(name: string): Promise<Blob | null>;
  remove(name: string): Promise<void>;
  list(): Promise<string[]>;
}

/* -------------------------------------------------------------------------- */
/* OPFS                                                                        */
/* -------------------------------------------------------------------------- */

function opfsAvailable(): boolean {
  if (typeof navigator === "undefined" || !("storage" in navigator)) return false;
  if (!("getDirectory" in navigator.storage)) return false;
  // Одного getDirectory мало: в Safari до 18.2 OPFS есть, а createWritable нет,
  // и запись падала бы вместо честного отката на IndexedDB.
  return (
    typeof FileSystemFileHandle !== "undefined" &&
    "createWritable" in FileSystemFileHandle.prototype
  );
}

const opfsStore: ArchiveStore = {
  kind: "opfs",

  async save(name, body, onChunk) {
    const dir = await (await navigator.storage.getDirectory()).getDirectoryHandle(OPFS_DIR, {
      create: true,
    });
    const handle = await dir.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();

    let written = 0;
    const reader = body.getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        // Поток fetch типизирован как Uint8Array<ArrayBufferLike>, а File System
        // Access API требует ArrayBufferView<ArrayBuffer>. Данные из сети всегда
        // лежат в обычном ArrayBuffer, а не в SharedArrayBuffer, — приведение безопасно.
        await writable.write(value as unknown as FileSystemWriteChunkType);
        written += value.byteLength;
        onChunk(written);
      }
      await writable.close();
    } catch (error) {
      // abort() откатывает запись целиком: файл остаётся в том виде, в каком
      // был до открытия writable. Удалять его отдельно нельзя — так терялась
      // бы ранее скачанная целая копия архива из-за обрыва повторной закачки.
      await writable.abort();
      throw error;
    }
    return written;
  },

  async read(name) {
    try {
      const dir = await (await navigator.storage.getDirectory()).getDirectoryHandle(OPFS_DIR);
      const handle = await dir.getFileHandle(name);
      return await handle.getFile();
    } catch {
      return null;
    }
  },

  async remove(name) {
    try {
      const dir = await (await navigator.storage.getDirectory()).getDirectoryHandle(OPFS_DIR);
      await dir.removeEntry(name);
    } catch {
      // Удалять нечего — это не ошибка.
    }
  },

  async list() {
    try {
      const dir = await (await navigator.storage.getDirectory()).getDirectoryHandle(OPFS_DIR);
      const names: string[] = [];
      for await (const key of (dir as unknown as { keys: () => AsyncIterable<string> }).keys()) {
        names.push(key);
      }
      return names;
    } catch {
      return [];
    }
  },
};

/* -------------------------------------------------------------------------- */
/* IndexedDB — запасной вариант для браузеров без OPFS                         */
/* -------------------------------------------------------------------------- */

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(IDB_STORE);
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(new Error("Не удалось открыть локальное хранилище браузера"));
    };
  });
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(new Error("Ошибка локального хранилища браузера"));
    };
  });
}

const idbStore: ArchiveStore = {
  kind: "indexeddb",

  async save(name, body, onChunk) {
    // IndexedDB не умеет дописывать: собираем архив в памяти и кладём целиком.
    // Это дороже OPFS по пиковой памяти — потому OPFS и основной вариант.
    const chunks: Uint8Array[] = [];
    let written = 0;
    const reader = body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      written += value.byteLength;
      onChunk(written);
    }

    const blob = new Blob(chunks as BlobPart[], { type: "application/octet-stream" });
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readwrite");
    await idbRequest(tx.objectStore(IDB_STORE).put(blob, name));
    db.close();
    return written;
  },

  async read(name) {
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readonly");
    const value = await idbRequest<unknown>(tx.objectStore(IDB_STORE).get(name));
    db.close();
    return value instanceof Blob ? value : null;
  },

  async remove(name) {
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readwrite");
    await idbRequest(tx.objectStore(IDB_STORE).delete(name));
    db.close();
  },

  async list() {
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readonly");
    const keys = await idbRequest(tx.objectStore(IDB_STORE).getAllKeys());
    db.close();
    return keys.filter((key): key is string => typeof key === "string");
  },
};

/* -------------------------------------------------------------------------- */
/* Публичный интерфейс                                                         */
/* -------------------------------------------------------------------------- */

const store: ArchiveStore = opfsAvailable() ? opfsStore : idbStore;

export function offlineSupported(): boolean {
  return opfsAvailable() || typeof indexedDB !== "undefined";
}

export function storageKind(): ArchiveStore["kind"] {
  return store.kind;
}

/** Имя файла в хранилище выводится из URL, чтобы архивы разных слоёв не пересекались. */
function storageName(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/[^a-zA-Z0-9._-]/g, "_");
}

export async function downloadArchive(
  url: string,
  onProgress: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<number> {
  const response = await fetch(url, signal ? { signal } : {});
  if (!response.ok) {
    throw new Error(`Сервер вернул ${response.status} при скачивании архива`);
  }
  if (!response.body) {
    throw new Error("Браузер не отдал поток загрузки — обновите его до актуальной версии");
  }

  const header = response.headers.get("content-length");
  const totalBytes = header ? Number(header) : null;

  return await store.save(storageName(url), response.body, (receivedBytes) => {
    onProgress({ receivedBytes, totalBytes });
  });
}

export async function removeArchive(url: string): Promise<void> {
  await store.remove(storageName(url));
}

export async function listArchives(): Promise<string[]> {
  return await store.list();
}

export async function isCached(url: string): Promise<boolean> {
  return (await store.read(storageName(url))) !== null;
}

/**
 * Подменяет сетевой источник локальным.
 *
 * Ключ источника совпадает с исходным URL, поэтому стиль карты и все уже
 * добавленные слои продолжают работать без единого изменения.
 */
export async function activateOffline(protocol: Protocol, url: string): Promise<boolean> {
  const blob = await store.read(storageName(url));
  if (!blob) return false;
  protocol.add(new PMTiles(new BlobSource(blob, url)));
  return true;
}

/** Сколько места занято под нужды сайта, по оценке браузера. */
export async function estimateUsage(): Promise<{ usage: number; quota: number } | null> {
  if (!("storage" in navigator) || !("estimate" in navigator.storage)) return null;
  const { usage, quota } = await navigator.storage.estimate();
  if (usage === undefined || quota === undefined) return null;
  return { usage, quota };
}
